import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageCircle, X, Check } from "lucide-react";
import { getSocket } from "../services/socket";

const MAX = 20; // max notifications to keep in memory

const formatRelative = (date) => {
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (diff < 60)  return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

// Single toast that slides in from the top-right
const ToastItem = ({ notif, onDismiss }) => (
  <div
    className="flex items-start gap-3 bg-white rounded-2xl shadow-2xl border border-gray-100 px-4 py-3 w-80 pointer-events-auto"
    style={{ animation: "slideInRight 0.35s cubic-bezier(.21,1.02,.73,1) forwards" }}
  >
    {/* Green dot + icon */}
    <div className="relative flex-shrink-0 mt-0.5">
      <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center">
        <MessageCircle className="w-4 h-4 text-white" />
      </div>
      <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-ping" />
      <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />
    </div>

    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold text-gray-900 truncate">
        {notif.customerName || notif.waId}
      </p>
      <p className="text-xs text-gray-500 truncate mt-0.5">{notif.preview}</p>
      <p className="text-[10px] text-gray-400 mt-1">{formatRelative(notif.at)}</p>
    </div>

    <button
      onClick={() => onDismiss(notif.id)}
      className="flex-shrink-0 p-1 rounded-full hover:bg-gray-100 text-gray-400 transition-colors mt-0.5"
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </div>
);

const LiveNotifications = () => {
  const [notifs, setNotifs]     = useState([]);   // persistent list for dropdown
  const [toasts, setToasts]     = useState([]);   // ephemeral toast queue
  const [open, setOpen]         = useState(false);
  const [unread, setUnread]     = useState(0);
  const dropdownRef             = useRef(null);
  const navigate                = useNavigate();
  const toastTimers             = useRef({});

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Listen for new inbound messages on the socket
  useEffect(() => {
    const socket = getSocket();

    const onNewMessage = ({ conversationId, message }) => {
      if (message.type !== "incoming") return; // only show for customer messages

      const notif = {
        id:           `${Date.now()}-${Math.random()}`,
        conversationId,
        waId:         message.waId,
        customerName: message.userName !== "Unknown" ? message.userName : null,
        preview:      message.messageType === "text"
                        ? (message.messageContent?.slice(0, 60) || "")
                        : `[${message.messageType}]`,
        at:           message.timestamp || new Date().toISOString(),
      };

      setNotifs((prev) => [notif, ...prev].slice(0, MAX));
      setUnread((n) => n + 1);

      // Show toast — auto-dismiss after 5s
      setToasts((prev) => [...prev, notif]);
      toastTimers.current[notif.id] = setTimeout(() => dismissToast(notif.id), 5000);
    };

    socket.on("new_message", onNewMessage);
    return () => socket.off("new_message", onNewMessage);
  }, []);

  const dismissToast = (id) => {
    clearTimeout(toastTimers.current[id]);
    delete toastTimers.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const openChat = (notif) => {
    setOpen(false);
    navigate("/chats");
  };

  const markAllRead = () => setUnread(0);

  const clearAll = () => { setNotifs([]); setUnread(0); };

  return (
    <>
      {/* ── Bell button ── */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => { setOpen((v) => !v); if (!open) markAllRead(); }}
          className="relative p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
          title="Notifications"
        >
          <Bell className={`w-5 h-5 ${unread > 0 ? "text-gray-700" : ""}`} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-white"
              style={{ animation: "popIn 0.3s cubic-bezier(.21,1.02,.73,1)" }}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>

        {/* ── Dropdown panel ── */}
        {open && (
          <div
            className="absolute right-0 top-11 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50"
            style={{ animation: "fadeSlideDown 0.2s ease forwards" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-800">Notifications</span>
                {notifs.length > 0 && (
                  <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-medium">
                    {notifs.length}
                  </span>
                )}
              </div>
              {notifs.length > 0 && (
                <button
                  onClick={clearAll}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1"
                >
                  <Check className="w-3 h-3" /> Clear all
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-[360px] overflow-y-auto">
              {notifs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-gray-300">
                  <Bell className="w-10 h-10 mb-2 opacity-40" />
                  <p className="text-sm text-gray-400">No notifications yet</p>
                </div>
              ) : (
                notifs.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => openChat(n)}
                    className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 text-left"
                  >
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MessageCircle className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">
                        {n.customerName || n.waId}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{n.preview}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{formatRelative(n.at)}</p>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Footer */}
            {notifs.length > 0 && (
              <div className="px-4 py-2.5 border-t border-gray-100">
                <button
                  onClick={() => { setOpen(false); navigate("/chats"); }}
                  className="w-full text-xs font-medium text-green-600 hover:text-green-700 transition-colors py-1"
                >
                  Open Chats →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Toast stack (top-right, outside dropdown) ── */}
      <div className="fixed top-16 right-4 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <ToastItem key={t.id} notif={t} onDismiss={dismissToast} />
        ))}
      </div>

      {/* ── Keyframe styles ── */}
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(110%); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes popIn {
          0%   { transform: scale(0); }
          70%  { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `}</style>
    </>
  );
};

export default LiveNotifications;
